import { defineConfig } from '@chakra-ui/react';

/**
 * Coach Hub fixed-foundation tokens, bridged to the vendored DS CSS variables
 * (defined in design-system/ds-runtime.css). The DS folder is the source of
 * truth — these tokens only forward to `var(--…)`. Brand colors are NOT here;
 * they are workspace-derived in theme.ts. Light mode only.
 */
export const dsFoundationConfig = defineConfig({
  // DS "Body & Labels — Roboto": reading copy is Roboto Light 15px, UI/controls
  // are Roboto Regular 14px. base.css (the DS global body reset) is intentionally
  // omitted from ds-runtime.css so it doesn't collide with Chakra's preflight, so
  // we reproduce the card's Body role here. <Text> renders <p>, so scoping Light
  // to <p> defaults prose to the DS Body role WITHOUT thinning inputs/buttons/
  // table cells (they aren't <p> and stay at the inherited Regular 400 — the DS
  // "UI/control" role, which the card warns must not be Light). Explicit
  // fontSize/fontWeight props on any element still override this element default.
  globalCss: {
    p: {
      fontSize: 'body', // 15
      fontWeight: 'light', // 300
    },
  },
  theme: {
    tokens: {
      fonts: {
        heading: { value: 'var(--font-display)' },
        body: { value: 'var(--font-body)' },
        mono: { value: 'var(--font-mono)' },
      },
      radii: {
        sm: { value: 'var(--r-sm)' },
        md: { value: 'var(--r-md)' },
        lg: { value: 'var(--r-lg)' },
        full: { value: 'var(--r-pill)' },
      },
      // DS spacing scale (4pt base). Only the 9 canonical steps exist in the DS
      // (4/8/12/16/20/24/32/40/48 — no s7/s9/s11, no half-steps). We remap the
      // matching Chakra numeric tokens onto the DS vars so spacing tracks the DS
      // source of truth (re-sync the CSS, don't edit numbers here). Chakra's
      // off-scale steps (0.5/1.5/2.5/3.5/7/9/11) are intentionally left at their
      // defaults but should be avoided at call sites — stay on the canonical steps.
      spacing: {
        1: { value: 'var(--s1)' }, // 4
        2: { value: 'var(--s2)' }, // 8
        3: { value: 'var(--s3)' }, // 12
        4: { value: 'var(--s4)' }, // 16
        5: { value: 'var(--s5)' }, // 20
        6: { value: 'var(--s6)' }, // 24
        8: { value: 'var(--s8)' }, // 32
        10: { value: 'var(--s10)' }, // 40
        12: { value: 'var(--s12)' }, // 48
      },
      shadows: {
        sm: { value: 'var(--shadow-sm)' },
        md: { value: 'var(--shadow-md)' },
        lg: { value: 'var(--shadow-lg)' },
      },
      letterSpacings: {
        eyebrow: { value: 'var(--track-eyebrow)' },
        label: { value: 'var(--track-label)' },
        display: { value: 'var(--track-display)' },
      },
      fontSizes: {
        // DS semantic type tokens.
        display: { value: 'var(--fs-display)' }, // 32
        h1: { value: 'var(--fs-h1)' }, // 24
        h2: { value: 'var(--fs-h2)' }, // 18
        h3: { value: 'var(--fs-h3)' }, // 16
        body: { value: 'var(--fs-body)' }, // 15
        ui: { value: 'var(--fs-ui)' }, // 14
        eyebrow: { value: 'var(--fs-eyebrow)' }, // 12
        label: { value: 'var(--fs-label)' }, // 11
        // Chakra's default size names, remapped DOWN onto the DS scale so the
        // whole app (e.g. Heading size="3xl", fontSize="md", recipe sizes)
        // tracks the DS sizes instead of Chakra's larger built-in defaults.
        // Sourced from the DS vars — no hardcoded numbers. 5xl+ (rare hero /
        // stat displays) keep their Chakra defaults so deliberately-large text
        // is not shrunk.
        xs: { value: 'var(--fs-eyebrow)' }, // 12 (was 12)
        sm: { value: 'var(--fs-sm)' }, // 13 (was 14)
        md: { value: 'var(--fs-body)' }, // 15 (was 16)
        lg: { value: 'var(--fs-h3)' }, // 16 (was 18)
        xl: { value: 'var(--fs-h2)' }, // 18 (was 20)
        '2xl': { value: 'var(--fs-h1)' }, // 24 (was 24)
        '3xl': { value: 'var(--fs-h1)' }, // 24 (was 30)
        '4xl': { value: 'var(--fs-display)' }, // 32 (was 36)
      },
      fontWeights: {
        light: { value: 'var(--fw-light)' },
        regular: { value: 'var(--fw-regular)' },
        medium: { value: 'var(--fw-medium)' },
        semibold: { value: 'var(--fw-semibold)' },
        bold: { value: 'var(--fw-bold)' },
        black: { value: 'var(--fw-black)' },
      },
      lineHeights: {
        tight: { value: 'var(--lh-tight)' },
        base: { value: 'var(--lh-base)' },
        relaxed: { value: 'var(--lh-relaxed)' },
      },
      colors: {
        danger: { value: 'var(--danger)' },
        success: { value: 'var(--success)' },
        warning: { value: 'var(--warning)' },
        badgeGray: {
          bg: { value: 'var(--badge-gray-bg)' },
          fg: { value: 'var(--badge-gray-fg)' },
        },
        slot1: { bg: { value: 'var(--slot1-bg)' }, fg: { value: 'var(--slot1-fg)' } },
        slot2: { bg: { value: 'var(--slot2-bg)' }, fg: { value: 'var(--slot2-fg)' } },
        slot3: { bg: { value: 'var(--slot3-bg)' }, fg: { value: 'var(--slot3-fg)' } },
        slot4: { bg: { value: 'var(--slot4-bg)' }, fg: { value: 'var(--slot4-fg)' } },
        slot5: { bg: { value: 'var(--slot5-bg)' }, fg: { value: 'var(--slot5-fg)' } },
        slot6: { bg: { value: 'var(--slot6-bg)' }, fg: { value: 'var(--slot6-fg)' } },
        slot7: { bg: { value: 'var(--slot7-bg)' }, fg: { value: 'var(--slot7-fg)' } },
        slot8: { bg: { value: 'var(--slot8-bg)' }, fg: { value: 'var(--slot8-fg)' } },
      },
    },
    semanticTokens: {
      // Chakra components reference l1/l2/l3 for corner radius; map them to DS scale
      radii: {
        l1: { value: '{radii.sm}' },
        l2: { value: '{radii.md}' },
        l3: { value: '{radii.lg}' },
      },
      colors: {
        bg: {
          DEFAULT: { value: 'var(--bg-page)' },
          subtle: { value: 'var(--bg-subtle)' },
          muted: { value: 'var(--bg-subtle)' },
          panel: { value: 'var(--bg-surface)' },
          overlay: { value: 'var(--bg-overlay)' },
        },
        info: {
          DEFAULT: { value: 'var(--info)' },
        },
        fg: {
          DEFAULT: { value: 'var(--text-body)' },
          strong: { value: 'var(--text-strong)' },
          muted: { value: 'var(--text-muted)' },
          subtle: { value: 'var(--text-faint)' },
        },
        border: {
          DEFAULT: { value: 'var(--border)' },
          emphasized: { value: 'var(--border-strong)' },
        },
        danger: {
          DEFAULT: { value: 'var(--danger)' },
          subtle: { value: 'var(--danger-bg)' },
        },
        success: {
          DEFAULT: { value: 'var(--success)' },
          subtle: { value: 'var(--success-bg)' },
        },
        warning: {
          DEFAULT: { value: 'var(--warning)' },
          subtle: { value: 'var(--warning-bg)' },
        },
      },
    },
    textStyles: {
      // DS "Body & Labels — Roboto" roles, codified so consumers/recipes can
      // reference them directly (e.g. Toast title uses textStyle 'ui').
      body: { value: {
        fontFamily: 'body', fontWeight: 'light', fontSize: 'body', lineHeight: 'base',
      }}, // reading copy · 15 / 300 Light
      ui: { value: {
        fontFamily: 'body', fontWeight: 'regular', fontSize: 'ui', lineHeight: 'base',
      }}, // UI / control · 14 / 400
      eyebrow: { value: {
        fontFamily: 'heading', fontWeight: '700', textTransform: 'uppercase',
        letterSpacing: 'eyebrow', fontSize: 'eyebrow', color: 'fg.muted',
      }},
      display: { value: {
        fontFamily: 'heading', fontWeight: '800', textTransform: 'uppercase',
        letterSpacing: 'display', lineHeight: '1.1', color: 'fg.strong',
      }},
    },
  },
});
